import { FunctionComponent, KeyboardEvent, useState } from "react";

import { classNames } from "@/utils/classNames";

/**
 * Minimal free-text chips input for the onboarding list configs:
 * Enter/comma adds a tag, ✕ (or backspace on empty input) removes.
 */
export const TagInput: FunctionComponent<{
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	isError?: boolean;
}> = ({ values, onChange, placeholder, isError }) => {
	const [draft, setDraft] = useState("");

	const commitDraft = () => {
		const value = draft.trim().replace(/,$/, "");

		if (value && !values.includes(value)) {
			onChange([...values, value]);
		}

		setDraft("");
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter" || event.key === ",") {
			event.preventDefault();
			commitDraft();
		} else if (event.key === "Backspace" && !draft && values.length > 0) {
			onChange(values.slice(0, -1));
		}
	};

	return (
		<div
			className={classNames(
				"input-styles flex h-auto min-h-[40px] w-full flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1.5",
				isError ? "border-status-error" : ""
			)}
		>
			{values.map((value) => (
				<span
					key={value}
					className="flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium"
					title={value}
				>
					<span className="max-w-[240px] truncate">{value}</span>
					<button
						className="cursor-pointer text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onChange(values.filter((entry) => entry !== value))}
					>
						×
					</button>
				</span>
			))}
			<input
				className="min-w-[120px] flex-1 border-none bg-transparent text-xs outline-none"
				placeholder={values.length === 0 ? placeholder : undefined}
				value={draft}
				onBlur={commitDraft}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={handleKeyDown}
			/>
		</div>
	);
};
